import React from 'react'
import { Snackbar, Alert } from '@mui/material'
import './issue-management.scss'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  TableSortLabel,
  Stack,
  TablePagination,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import IssueForm from './components/IssueForm/IssueForm'
import IssueDetail from './components/IssueDetail/IssueDetail'
import IssueDashboard from './components/IssueDashboard/IssueDashboard'

const IssueManagementDesktop = ({ logic }) => {
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const { state, setters, actions, utils, options, permissions } = logic
  const { can } = permissions

  // Destructure for easier access in template
  const {
    loading,
    statusFilter, priorityFilter, escalationFilter,
    plantFilter, issueTypeFilter, categoryFilter, dateRange,
    assignedToFilter,
    sortBy, sortOrder,
    showForm, showDetail, showDeleteModal,
    selectedIssue, editMode,
    issueToDelete,
    snackbar,
    filteredIssues, // Keep filteredIssues for export/dashboard
    tab, // Keep tab for tab selection
    isExporting, // Keep isExporting for export button
    plantOptions // Keep plantOptions for filter
  } = state

  const {
    handleCreateIssue, handleEditIssue, handleViewIssue, handleDeleteIssue,
    confirmDeleteIssue, handleCancelDelete, handleFormSubmit, handleFormClose,
    handleDetailClose, handleTabChange, handleSort, exportToCSV
  } = actions

  const {
    getPriorityColor, getStatusColor, getEscalationLevelStyle
  } = utils

  const {
    statusOptions, priorityOptions, issueTypeOptions, categoryOptions,
    escalationOptions, assignedToOptions
  } = options

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <div className="issue-management">
      <div className="issue-management-header">
        <Typography variant="h4" component="h1" className="page-title">
          Issue Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {tab === 1 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={exportToCSV}
              disabled={filteredIssues.length === 0 || isExporting}
              className="export-issues-btn"
            >
              Download ({filteredIssues.length})
            </Button>
          )}
          {can('issue.create') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateIssue}
              className="create-issue-btn"
            >
              Create Issue
            </Button>
          )}
        </Box>
      </div>

      {/* Tabs: Dashboard / Table */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="Issue Management tabs"
        >
          <Tab label="Dashboard" />
          <Tab label="Table" />
        </Tabs>
      </Box>

      {/* Content depending on selected tab */}
      {tab === 0 && (
        // Dashboard tab: only show dashboard cards
        <IssueDashboard issues={filteredIssues} />
      )}

      {tab === 1 && (
        <>
          {/* Filters Section */}
          <Card className="filters-card" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    //flexWrap: 'wrap',
                    minWidth: '1200px',
                    pb: 1,
                    pt: 1,
                  }}
                >
                  {/* Plant Filter */}
                  <Autocomplete
                    multiple
                    size="small"
                    options={plantOptions}
                    getOptionLabel={(option) => option.name}
                    value={plantOptions.filter((plant) =>
                      plantFilter.includes(plant.id)
                    )}
                    onChange={(event, newValue) => {
                      setters.setPlantFilter(newValue.map((plant) => plant.id))
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Plant"
                        placeholder="Type to search..."
                      //sx={{minWidth: '200px'}}
                      />
                    )}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase()
                      return options.filter(
                        (option) =>
                          option.name.toLowerCase().includes(inputValue) ||
                          option.id.toLowerCase().includes(inputValue)
                      )
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    sx={{ minWidth: 200 }}
                  />

                  {/* Status Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setters.setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Priority Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={priorityFilter}
                      onChange={(e) => setters.setPriorityFilter(e.target.value)}
                      label="Priority"
                    >
                      <MenuItem value="all">All Priorities</MenuItem>
                      {priorityOptions.map((priority) => (
                        <MenuItem key={priority} value={priority}>
                          {priority}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Escalation Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Escalation</InputLabel>
                    <Select
                      value={escalationFilter}
                      onChange={(e) => setters.setEscalationFilter(e.target.value)}
                      label="Escalation"
                    >
                      <MenuItem value="all">All Levels</MenuItem>
                      {escalationOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Issue Type Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={issueTypeFilter}
                      onChange={(e) => setters.setIssueTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {issueTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Category Filter */}
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setters.setCategoryFilter(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {categoryOptions.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Assigned To Filter */}
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Assigned To (Int.)</InputLabel>
                    <Select
                      value={assignedToFilter}
                      onChange={(e) => setters.setAssignedToFilter(e.target.value)}
                      label="Assigned To (Int.)"
                    >
                      <MenuItem value="all">All Assignees</MenuItem>
                      {assignedToOptions.map((user) => (
                        <MenuItem key={user} value={user}>
                          {user}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Date Range Picker */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From"
                      format="DD-MM-YYYY"
                      value={dateRange.from}
                      onChange={(date) =>
                        setters.setDateRange((prev) => ({ ...prev, from: date }))
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            width: 160,
                            flexShrink: 0,
                          },
                        },
                      }}

                    />
                    <DatePicker
                      label="To"
                      format="DD-MM-YYYY"
                      value={dateRange.to}
                      onChange={(date) =>
                        setters.setDateRange((prev) => ({ ...prev, to: date }))
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: {
                            width: 160,
                            flexShrink: 0,
                          },
                        },
                      }}

                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Issues Table */}
          <Card className="issues-table-card" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer component={Paper}>
                  <Table className="issues-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Sr. No</TableCell>
                        <TableCell
                          onClick={() => handleSort('plantName')}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableSortLabel
                            active={sortBy === 'plantName'}
                            direction={sortBy === 'plantName' ? sortOrder : 'asc'}
                          >
                            Site Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Plant Capacity (kWp)</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Issue Type</TableCell>
                        <TableCell
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableSortLabel
                            active={sortBy === 'createdAt'}
                            direction={sortBy === 'createdAt' ? sortOrder : 'asc'}
                          >
                            Date Reported
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Reported By</TableCell>
                        <TableCell>Assigned To (Internal)</TableCell>
                        <TableCell>Assigned To (External)</TableCell>
                        <TableCell>Expected Closure Date</TableCell>
                        <TableCell>Actual Closure Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Overdue (Days)</TableCell>
                        <TableCell>Impact (Generation Loss / Risk)</TableCell>
                        <TableCell>Escalation Level</TableCell>
                        <TableCell>Customer Communication Done (Y/N)</TableCell>
                        <TableCell>Proof of Closure</TableCell>
                        <TableCell>Action Plan</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredIssues
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((issue, index) => {
                          const esc = getEscalationLevelStyle(issue.escalationLevel)

                          return (
                            <TableRow key={issue.id} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 500, minWidth: 150 }}>
                                {issue.plantName || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {issue.plantCapacity || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>
                                {issue.category || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={issue.priority || 'N/A'}
                                  color={getPriorityColor(issue.priority)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>
                                {issue.issueType || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {issue.createdAt
                                  ? dayjs(issue.createdAt).format('DD-MM-YYYY')
                                  : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>
                                {issue.reportedBy || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>
                                {issue.assignedTo || 'Unassigned'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>
                                {issue.responsibleTeamExternal || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {issue.expectedClosingDate
                                  ? dayjs(issue.expectedClosingDate).format('DD-MM-YYYY')
                                  : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {issue.actualClosingDate
                                  ? dayjs(issue.actualClosingDate).format('DD-MM-YYYY')
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={issue.status || 'N/A'}
                                  color={getStatusColor(issue.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {issue.delayDays > 0 ? (
                                  <Chip label={`${issue.delayDays}d`} color="error" size="small" variant="outlined" />
                                ) : (issue.delayDays === 0 ? '0' : '-')}
                              </TableCell>
                              <TableCell sx={{ minWidth: 150 }}>
                                {issue.severityImpact || 'N/A'}
                              </TableCell>
                              <TableCell>
                                {issue.escalationLevel ? (
                                  <Tooltip title={esc.label}>
                                    <Chip
                                      label={issue.escalationLevel}
                                      size="small"
                                      color={esc.color}
                                      icon={<span>{esc.icon}</span>}
                                      sx={{ minWidth: 80, justifyContent: 'flex-start' }}
                                    />
                                  </Tooltip>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                {typeof issue.customerCommunicationDone === 'boolean'
                                  ? (issue.customerCommunicationDone ? 'Y' : 'N')
                                  : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 120 }}>
                                {issue.proofOfClosure || '-'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 150, maxWidth: 200 }}>
                                <Typography variant="body2" noWrap>
                                  {issue.nextActionFollowUpPlan || 'N/A'}
                                </Typography>
                              </TableCell>
                              {/* Actions Column */}
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewIssue(issue)}
                                    title="View Details"
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>

                                  {can('issue.update') && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditIssue(issue)}
                                      title="Edit Issue"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}

                                  {can('issue.delete') && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteIssue(issue)}
                                      title="Delete Issue"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      {filteredIssues.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={20} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No issues found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredIssues.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10))
                  setPage(0)
                }}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    px: { xs: 1, sm: 2 },
                    minHeight: '52px',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    mb: 0,
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Issue Form Dialog */}
      <Dialog
        open={showForm}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleFormClose()
        }}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={true} // Helps with nested input interaction
      >
        <DialogTitle>
          {editMode ? 'Edit Issue' : 'Create New Issue'}
          <IconButton
            onClick={handleFormClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <IssueForm
            issue={selectedIssue}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
            categoryOptions={categoryOptions}
            priorityOptions={priorityOptions}
            statusOptions={statusOptions}
            escalationOptions={escalationOptions}
            issueTypeOptions={issueTypeOptions}
            assignedToOptions={assignedToOptions}
          />
        </DialogContent>
      </Dialog>

      {/* Issue Detail Dialog */}
      <Dialog
        open={showDetail}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleDetailClose()
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Issue Details
          <IconButton
            onClick={handleDetailClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <IssueDetail
              issue={selectedIssue}
              onEdit={() => {
                setters.setShowDetail(false)
                handleEditIssue(selectedIssue)
              }}
              onClose={handleDetailClose}
              setSnackbar={setters.setSnackbar}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleCancelDelete()
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Issue
          <IconButton
            onClick={handleCancelDelete}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete issue{' '}
            <strong>{issueToDelete?.title}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteIssue}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setters.setSnackbar({ open: false, message: '', severity: 'success' })}
      >
        <Alert
          onClose={() => setters.setSnackbar({ open: false, message: '', severity: 'success' })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div >
  )
}

export default IssueManagementDesktop